import { queryOptions } from '@tanstack/react-query';

import { apiAdminViewsFetch, apiUserFetch, handleApiResponseErrorWithoutToast } from '..';
import type {
  AdminUserKycRes,
  AdminUserTag,
  AdminViewsEntitiesParams,
  AdminViewsEntitiesRes,
  AdminViewsFundsParams,
  AdminViewsFundsRes,
  AdminViewsUsersParams,
  AdminViewsUsersRes,
  UserStatsRes,
} from './types';

export const ADMIN_USERS_QUERY_KEY = {
  adminUserStats: ['admin', 'users', 'stats'] as const,
};

////////////////////////// USERS //////////////////////////

export const ADMIN_VIEWS_QUERY_KEY = {
  usersRoot: ['admin-views', 'users'] as const,
  users: (params: AdminViewsUsersParams) => ['admin-views', 'users', params] as const,
  userKyc: (userId: string | undefined) => ['admin-views', 'users', userId, 'kyc'] as const,
};

export const _getAdminViewsUserKyc = (userId: string | undefined) =>
  queryOptions({
    enabled: !!userId,
    refetchOnMount: true,
    queryKey: ADMIN_VIEWS_QUERY_KEY.userKyc(userId),
    queryFn: () => apiAdminViewsFetch<AdminUserKycRes>(`/users/${userId}/kyc`),
  });

export const _getAdminViewsUsers = (params: AdminViewsUsersParams = {}) =>
  queryOptions({
    refetchOnMount: true,
    queryKey: ADMIN_VIEWS_QUERY_KEY.users(params),
    queryFn: () =>
      apiAdminViewsFetch<AdminViewsUsersRes>('/users', {
        params,
      }),
  });

type GammaUserLookup = { id: string; email: string; name: string | null };

export const _getGammaUserByEmail = (email: string) =>
  queryOptions({
    queryKey: ['admin', 'gamma-user', 'email', email],
    queryFn: async (): Promise<GammaUserLookup | null> => {
      try {
        return await apiUserFetch<GammaUserLookup>(`/user/email/${encodeURIComponent(email)}`, {
          onResponseError: handleApiResponseErrorWithoutToast,
        });
      } catch (error) {
        if ((error as { response?: { status?: number } }).response?.status === 404) return null;
        throw error;
      }
    },
    retry: false,
  });

export const _getGammaUserById = (userId: string) =>
  queryOptions({
    queryKey: ['admin', 'gamma-user', 'id', userId],
    queryFn: () =>
      apiUserFetch<GammaUserLookup>(`/user/${encodeURIComponent(userId)}`, {
        onResponseError: handleApiResponseErrorWithoutToast,
      }),
    retry: false,
  });

export const ADMIN_USER_TAGS_QUERY_KEY = {
  tags: ['admin', 'users', 'tags'] as const,
};

export const _getAdminUserTags = () =>
  queryOptions({
    queryKey: ADMIN_USER_TAGS_QUERY_KEY.tags,
    queryFn: () => apiUserFetch<AdminUserTag[]>('/admin/users/tags'),
  });

////////////////////////// ENTITIES //////////////////////////

export const ADMIN_VIEWS_ENTITIES_QUERY_KEY = {
  entitiesRoot: ['admin-views', 'entities'] as const,
  entities: (params: AdminViewsEntitiesParams) => ['admin-views', 'entities', params] as const,
};

export const _getAdminViewsEntities = (params: AdminViewsEntitiesParams = {}) =>
  queryOptions({
    refetchOnMount: true,
    queryKey: ADMIN_VIEWS_ENTITIES_QUERY_KEY.entities(params),
    queryFn: () =>
      apiAdminViewsFetch<AdminViewsEntitiesRes>('/entities', {
        params,
      }),
  });

////////////////////////// FUNDS //////////////////////////

export const ADMIN_VIEWS_FUNDS_QUERY_KEY = {
  fundsRoot: ['admin-views', 'funds'] as const,
  funds: (params: AdminViewsFundsParams) => ['admin-views', 'funds', params] as const,
};

export const _getAdminViewsFunds = (params: AdminViewsFundsParams = {}) =>
  queryOptions({
    refetchOnMount: true,
    queryKey: ADMIN_VIEWS_FUNDS_QUERY_KEY.funds(params),
    queryFn: () =>
      apiAdminViewsFetch<AdminViewsFundsRes>('/funds', {
        params,
      }),
  });

////////////////////////// STATS //////////////////////////

export const _getAdminViewsUserStats = () => {
  return queryOptions({
    refetchOnMount: true,
    queryKey: ADMIN_USERS_QUERY_KEY.adminUserStats,
    queryFn: () => apiAdminViewsFetch<UserStatsRes>('/users/stats'),
  });
};
